<template>
  <v-card class="ma-4">
    <v-toolbar>
      <v-toolbar-title>Program Change for: {{ name }} ({{ bannerId }}) - {{ termLabel }}</v-toolbar-title>
      <v-spacer />
      <v-dialog v-if="status === 'Pending' || status === 'Submitted'" v-model="returnDialog" width="500">
        <template #activator="{ on }">
          <v-btn v-on="on" class="mr-4">Return to Student</v-btn>
        </template>
        <v-card>
          <v-card-title>Return to Student</v-card-title>
          <v-card-text>
            <v-switch v-model="returnSendEmail" :label="returnSendEmail ? 'Send Email to Student' : 'Do Not Email Student'" />
            <v-textarea v-model="returnMessage" :label="returnSendEmail ? 'Email Message to Student (Required)' : 'Message for Timeline (Optional; will be visible to student)'" variant="outlined" rows="3" />
          </v-card-text>
          <v-card-actions>
            <v-btn :disabled="returnSendEmail && returnMessage.length < 10" @click="returnToStudent()">Send Message and Return</v-btn>
            <v-spacer />
            <v-btn @click="returnDialog = false">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-else-if="isRecords && status === 'Approved'" v-model="processDialog" width="500">
        <template #activator="{ on }">
          <v-btn v-on="on" color="info" class="mr-4">Mark as Processed</v-btn>
        </template>
        <v-card>
          <v-card-title>Mark as Processed</v-card-title>
          <v-card-text>
            <p>If you enter a message in the box below, the system will send an email to the student to let them know the change request has been completed, along with the message you enter. If you do not want to send an email, leave the box blank.</p>
            <v-textarea v-model="processComment" label="Message to Email to Student (optional)" outlined rows="4" />
          </v-card-text>
          <v-card-actions>
            <v-btn color="info" @click="markProcessed()">Mark Processed</v-btn>
            <v-spacer />
            <v-btn text @click="processDialog = false">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      Status: {{ status }}
    </v-toolbar>
    <v-card-text>
      <v-row>
        <v-col :cols="12" md="6">
          <h2>Current Program Information:</h2>
          <v-row class="my-2">
            <v-col v-for="{ major, majorDesc, advisors, concentrations, availableConcentrations } of current.programs" :key="major">
              <v-card>
                <v-card-text>
                  <v-list>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle>Major</v-list-item-subtitle>
                        <v-list-item-title>{{ majorDesc }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle>Advisor{{ advisors.length > 1 ? 's' : '' }}</v-list-item-subtitle>
                        <v-list-item-title v-for="{ text, value } in advisors" :key="'advr-' + value">{{ text }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-if="concentrations.length > 0 || availableConcentrations.length > 0">
                      <v-list-item-subtitle v-if="concentrations.length === 0">No Concentrations Selected</v-list-item-subtitle>
                      <v-list-item-content v-else>
                        <v-list-item-subtitle>Concentration{{ concentrations.length === 1 ? '' : 's' }}</v-list-item-subtitle>
                        <v-list-item-title v-for="{ text, value } of concentrations" :key="'conc-' + value">{{ text }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col v-if="current.minors.length > 0">
              <v-card>
                <v-card-text>
                  <v-list>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle>Minor{{ current.minors.length === 1 ? '' : 's' }}</v-list-item-subtitle>
                        <v-list-item-title v-for="{ value, text } in current.minors" :key="'minor-' + value">{{ text }}</v-list-item-title>
                        <v-list-item-title v-if="current.minors.length === 0">**No declared minors**</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-divider />
          <h2 class="mt-4 mb-2">Requested Changes:</h2>
          <v-alert v-for="{ _id, action, field, text, value } of changes" :key="action + '-' + field + '-' + value" :color="action === 'add' ? 'success' : (action === 'drop' ? 'error' : 'info')" colored-border border="left" elevation="3">
            <template #prepend>
              <v-icon v-if="action === 'add'" class="mr-4">fas fa-plus-circle</v-icon>
              <v-icon v-else-if="action === 'drop'" class="mr-4">fas fa-minus-circle</v-icon>
              <v-icon v-else class="mr-4">fas fa-exchange</v-icon>
            </template>
            <span v-html="text" />
            <template #append v-if="isRecords && status === 'Submitted' && action !== 'drop' && ['major', 'advisor'].includes(field)">
              <v-btn @click="openAdvisorChangeDialog(_id)">Change Advisor</v-btn>
            </template>
          </v-alert>
          <v-dialog v-model="changeAdvisorDialog" width="400">
            <v-card>
              <v-card-title>Change Advisor</v-card-title>
              <v-card-text>
                <directory-search v-model="newAdvisor" search-label="Advisor Search" :person-filter="['Faculty']" name-format-last-first />
              </v-card-text>
              <v-card-actions>
                <v-btn :disabled="!newAdvisor" @click="changeAdvisor()">Set Advisor</v-btn>
                <v-spacer />
                <v-btn @click="changeAdvisorDialog = false">Cancel</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
        <v-col :cols="12" :md="6">
          <v-card v-if="(status === 'Submitted' || status === 'Pending' || status === 'Approved' || status === 'Completed') && advisorApprovals.length > 0" class="mb-4">
            <v-card-text>
              <h2>Advisor Approvals</h2>
            </v-card-text>
            <v-list>
              <v-list-item v-for="{ pidm, name, approved, removed, date } of advisorApprovals" :key="pidm">
                <v-list-item-content :style="removed ? 'text-decoration:line-through' : ''">
                  <v-list-item-title>{{ name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="date">Approved on: {{  toDateString(date) }}</v-list-item-subtitle>
                  <v-list-item-subtitle v-else>Not Approved</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <template v-if="isRecords && status === 'Submitted'">
                    <div style="flex:none">
                      <v-tooltip v-if="removed" top>
                        <template #activator="{ on }">
                          <v-btn v-on="on" icon style="display:inline-block;" @click="advisorApprovalAdd(pidm)">
                            <v-icon small color="success">fal fa-plus-circle</v-icon>
                          </v-btn>
                        </template>
                        <span>Add Advisor back to Approvals</span>
                      </v-tooltip>
                      <template v-else>
                        <v-tooltip top>
                          <template #activator="{ on }">
                            <v-btn v-on="on" icon style="display:inline-block;" @click="advisorApprovalChange(pidm)">
                              <v-icon small color="info">fal fa-edit</v-icon>
                            </v-btn>
                          </template>
                          <span>Change Advisor</span>
                        </v-tooltip>
                        <v-tooltip top>
                          <template #activator="{ on }">
                            <v-btn v-on="on" icon style="display:inline-block;" @click="advisorApprovalRemove(pidm)">
                              <v-icon small color="error">fal fa-minus-circle</v-icon>
                            </v-btn>
                          </template>
                          <span>Remove Advisor from Approvals</span>
                        </v-tooltip>
                      </template>
                    </div>
                  </template>
                  <template v-else-if="isRecords && status === 'Pending' && !approved">
                    <v-tooltip top>
                      <template #activator="{ on }">
                        <v-btn v-on="on" color="success" @click="grantAdvisorApproval(pidm, name)">
                          <v-icon small left>fal fa-check-circle</v-icon>
                          Mark Approved
                        </v-btn>
                      </template>
                      Grant the approval on behalf of {{ name }}
                    </v-tooltip>
                  </template>
                  <v-icon v-else-if="approved" color="success">fas fa-check-circle</v-icon>
                  <v-icon v-else color="info">fas fa-question-circle</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
            <v-card-actions v-if="isRecords && status === 'Submitted'">
              <v-btn @click="advisorApprovalChange('')">Add Advisor to Approvals</v-btn>
              <v-spacer />
              <v-btn :disabled="isSendingToAdvisors" color="info" @click="sendToAdvisors()">{{ isSendingToAdvisors ? 'Sending Email...' : 'Send to Advisors' }}</v-btn>
            </v-card-actions>
            <v-card-actions v-else-if="isPendingApprover">
              <v-btn color="info" @click="grantAdvisorApproval()">Approve Request</v-btn>
            </v-card-actions>
          </v-card>
          <h3>Activity</h3>
          <v-card v-if="showAddMessage" class="my-2">
            <v-card-text>
              <h3>Add a Message</h3>
              <v-textarea v-model="comment" label="Message" style="margin-top:1em" rows="3" outlined hide-details />
              <v-switch v-model="visibleToStudent" :label="`Message should be ${visibleToStudent ? 'visible to' : 'hidden from'} ${name}`" :disabled="emailStudent" hide-details @change="(val) => { if (val) emailStudent = false }" />
              <v-checkbox v-model="emailStudent" :label="'Email this message to ' + name" :disabled="!visibleToStudent" hide-details @change="(val) => { if (val) visibleToStudent = true }" />
              <v-checkbox v-model="emailAdvisors" label="Email this message to all of the advisor(s)" hide-details />
            </v-card-text>
            <v-card-actions>
              <v-btn :disabled="comment.length < 10" color="info" @click="addComment">{{ emailStudent || emailAdvisors ? 'Send' : 'Add' }} Message</v-btn>
              <v-spacer></v-spacer>
              <v-btn @click="showAddMessage = false">Cancel</v-btn>
            </v-card-actions>
          </v-card>
          <v-btn v-else class="my-2" @click="showAddMessage = true">Add Message</v-btn>
          <v-timeline dense>
            <v-timeline-item v-for="({ name, date, text, icon, color, visibleToStudent }, index) in timeline" :key="'timeline-' + index" :color="color">
              <template v-slot:icon>
                <v-icon small color="white">{{ icon }}</v-icon>
              </template>
              <v-card>
                <v-card-text>
                  <v-row>
                    <v-col>{{ name }}</v-col>
                    <v-col class="text-right">{{ toDateString(date) }}</v-col>
                  </v-row>
                  <v-divider style="margin: .2em 0"></v-divider>
                  <p :style="visibleToStudent ? '' : 'font-style:italic'" style="margin-bottom:0" v-html="text"></p>
                  <template v-if="!visibleToStudent">
                    <v-divider style="margin: .2em 0"></v-divider>
                    <p style="font-weight:bold;margin-bottom:0">Not visible to student</p>
                  </template>
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>
<script>
import { ref, reactive, computed, onMounted } from '@vue/composition-api'

export default {
  components: {
    DirectorySearch: () => import('@/components/greatscots/searchField')
  },
  setup (props, { root }) {
    const service = root.$feathers.service('student/major-change')
    const settingServ = root.$feathers.service('student/reg-setting')

    function commentBox (comment) {
      return `<div style="border:2px solid #EEE;margin-top:20px;margin-bottom:20px;max-width:600px;"><div style="border:2px solid #DDD"><div style="border:1px solid #CCC;padding:24px;">${comment.replaceAll('\n', '<br/>')}</div></div></div>`
    }
    const studentLink = computed(() => `<a href="https://portal.covenant.edu/student/program-change/status" style="padding:12px;background-color:#193264 !important;text-decoration:none;color:white;height:36px;">View this request in the Portal</a>`)
    const employeeLink = computed(() => `<a href="https://portal.covenant.edu/student/program-change/admin/${id.value}" style="padding:12px;background-color:#193264 !important;text-decoration:none;color:white;height:36px;">View this request in the Portal</a>`)
    const signature = ref('<br/>Thanks,<br/><br/>Records Office<br/>')

    const id = computed(() => root.$route.params.id)
    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const roles = computed(() => user.value.roles)

    const isRecords = computed(() => !!roles.value.find((role) => role === 'Records' || role === 'Technology Services'))
    const isPendingApprover = computed(() => advisorApprovals.value.find(({ pidm, approved }) => pidm === user.value.pidm && !approved))

    const current = reactive({
      programs: [],
      minors: [],
      p12: false,
      contentField: []
    })
    const dataLoaded = ref(false)
    const pidm = ref('')
    const term = ref('')
    const termLabel = ref('')
    const bannerId = ref('')
    const name = ref('')
    const status = ref('')
    const changes = ref([])
    const timeline = ref([])
    const advisorApprovals = ref([])

    const majorItems = ref([])
    const minorItems = ref([])

    onMounted(async () => {
      if (!roles.value.find((role) => ['Records', 'Employee', 'All Adjuncts'].includes(role))) {
        root.$router.replace('/student/program-change/')
        return
      }

      const record = await service.get(id.value)
      pidm.value = record.pidm
      term.value = record.term
      root.$feathers.service('system/term').find({ query: { term: term.value } }).then(({ data }) => { termLabel.value = data.length > 0 ? data[0].label : '' })
      bannerId.value = record.bannerId
      name.value = record.name
      status.value = record.status
      timeline.value = record.timeline.sort((a, b) => { return new Date(a.date) < new Date(b.date) ? 1 : -1 })
      changes.value = []
      for (const rec of record.changes) {
        const { action, field, value, prev, advisor, concentrations } = rec
        let text = action.substring(0, 1).toUpperCase() + action.substring(1) + ' ' + field.substring(0, 1).toUpperCase() + field.substring(1)
        if (action === 'change') {
          text += `<br/> &nbsp; from ${field === 'advisor' ? await getAdvrName(prev) : await lookupValue(field, prev)}`
          text += '<br/> &nbsp; to ' + (field === 'advisor' ? await getAdvrName(value) : await lookupValue(field, value))
        } else {
          text += '<br/>' + (field === 'advisor' ? await getAdvrName(value) : await lookupValue(field, value))
        }
        if (field === 'major') {
          if (advisor) text += '<br/> &nbsp; &nbsp; Advisor: ' + await getAdvrName(advisor)
          if (concentrations && concentrations.length > 0) {
            const temp = []
            for (const conc of concentrations) {
              temp.push(await lookupValue('concentration', conc))
            }
            text += '<br/> &nbsp; &nbsp; Concentrations: ' + temp.join('; ')
          } else {
            text += '<br/> &nbsp; &nbsp; No Concentrations Selected'
          }
        }
        changes.value.push({ ...rec, text })
      }

      settingServ.find({ query: { form: 'program', category: 'major', $sort: { term: -1 }, $limit: 1 } }).then(({ data }) => {
        majorItems.value = data[0].options
      })
      // Load the minor options
      settingServ.find({ query: { form: 'program', category: 'minor', $sort: { term: -1 }, $limit: 1 } }).then(({ data }) => {
        minorItems.value = data[0].options
      })

      const { data: studentData } = await root.$feathers.service('student/term-detail').find({ query: { pidm: pidm.value, term: term.value } })
      if (studentData.length > 0) {
        let { academics, term: termCode } = studentData[0]
        term.value = termCode
        const { data: termData } = await root.$feathers.service('system/term').find({ query: { term: termCode } })
        if (termData.length > 0) termLabel.value = termData[0].label
        setInitialData(academics)
        dataLoaded.value = true
      }

      if (!record.advisorApproval || record.advisorApproval.length === 0) {
        // Figure out who should be involved in this; this include their current advisor(s) and any new advisors added/changed
        advisorApprovals.value = studentData[0].academics.advisor.map(({ pidm, name }) => { return { pidm, name, approved: false } })
        for (const { field, value, advisor } of changes.value) {
          if ((advisor && !advisorApprovals.value.find(({ pidm }) => pidm === advisor)) || (field === 'advisor' && !advisorApprovals.value.find(({ pidm }) => pidm === value))) {
            const name = await getAdvrName(advisor || value)
            advisorApprovals.value.push({ pidm: advisor, name, approved: false })
          }
        }
        await service.patch(id.value, { $push: { advisorApproval: advisorApprovals.value } })
      } else {
        advisorApprovals.value = record.advisorApproval
      }
    })

    async function setInitialData ({ major, minor, concentration, advisor }) {
      current.programs = []

      let isFirst = true
      for (const maj of major) {
        const majorDesc = await lookupValue('major', maj)
        const prog = {
          major: maj,
          majorDesc,
          advisors: [],
          concentrations: [],
          availableAdvisors: [],
          availableConcentrations: []
        }
        const { data } = await settingServ.find({ query: { form: 'program', major: maj, $sort: { category: 1, term: -1 } } })
        for (const { category, options } of data) {
          if (category === 'advisor') {
            // Find any major advisors in the advisor list and match them to what's in the options list
            prog.availableAdvisors = options
            const arr = options.filter(({ value }) => advisor.find(({ code, pidm }) => code.substr(0, 3) === 'MAJ' && pidm === value))
            if (arr.length === 0) {
              const advrPidm = advisor.find(({ code }) => code === (isFirst ? 'MAJR' : 'MAJ2')).pidm
              const text = await getAdvrName(advrPidm)
              prog.advisors = [{ value: advrPidm, text }]
            } else {
              prog.advisors = arr
            }
          } else if (category === 'concentration') {
            prog.concentrations = options.filter(({ value }) => concentration.includes(value))
            prog.availableConcentrations = options
          }
        }
        current.programs.push(prog)
        isFirst = false
      }
      current.minors = minorItems.value.filter(({ value }) => minor.includes(value))
    }

    async function getAdvrName (pidm) {
      const { data: dirData } = await root.$feathers.service('directory/people').find({ query: { pidm, $select: ['name'] } })
      if (dirData.length > 0) {
        return dirData[0].name.last + ', ' + dirData[0].name.first
      }
      return 'Advisor Not Found'
    }
    async function getEmail (pidm) {
      const query = { pidm, $select: ['email'] }
      if (Array.isArray(pidm)) {
        query.pidm = { $in: pidm }
      }
      const { data } = await root.$feathers.service('directory/people').find({ query: { pidm: { $in: pidm }, $select: ['email'] } })
      if (data.length > 0) {
        return data.map(({ email }) => email)
      }
    }
    async function lookupValue (category, value) {
      const aggregate = [
        { $match: {
          form: 'program',
          category
        } },
        { $unwind: '$options' },
        { $match: {
          'options.value': Array.isArray(value) ? { $in: value } : value
        } },
        { $project: {
          value: '$options.value',
          text: '$options.text'
        } }
      ]
      const { data } = await settingServ.find({ query: { aggregate } })
      if (data.length > 0) return data[0].text
    }

    const showAddMessage = ref('')
    const comment = ref('')
    const visibleToStudent = ref(true)
    const emailStudent = ref(false)
    const emailAdvisors = ref(false)

    async function addComment () {
      const timelineObj = {
        pidm: user.value.pidm,
        name: user.value.name,
        date: new Date(),
        text: comment.value,
        icon: 'fal fa-comment',
        color: 'info',
        visibleToStudent: visibleToStudent.value
      }
      await service.patch(id.value, { $push: { timeline: timelineObj } })
      timeline.value.splice(0, 0, timelineObj)
      if (emailStudent.value) {
        await root.$feathers.service('system/email').create({
          to: await getEmail(pidm.value),
          from: 'recordsoffice@covenant.edu',
          subject: 'Message about Your Program Change Request',
          html: `${user.value.name} just added a message to your program/advisor change submission. Here is the message:<br/>${commentBox(comment.value)}<br/>${studentLink.value}<br/>${signature.value}`
        })
      }
      if (emailAdvisors.value) {
        await root.$feathers.service('system/email').create({
          to: await getEmail(advisorApprovals.value.filter(({ removed }) => !removed).map(({ pidm }) => pidm)),
          from: 'recordsoffice@covenant.edu',
          subject: 'Message about a Pending Program Change Request',
          html: `${user.value.name} just added a message to a pending program/advisor change that you are associated with. Here is the message:<br/>${commentBox(comment.value)}<br/>${employeeLink.value}<br/>${signature.value}`
        })
      }
    }

    function toDateString (date) {
      return new Date(date).toLocaleString('en-CA', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const advisorChangeId = ref('')
    const changeAdvisorDialog = ref(false)
    const newAdvisor = ref('')
    function openAdvisorChangeDialog (id) {
      advisorChangeId.value = id
      newAdvisor.value = ''
      changeAdvisorDialog.value = true
    }
    async function changeAdvisor () {
      const newName = newAdvisor.value.text
      const { pidm } = await root.$feathers.service('directory/people').get(newAdvisor.value.value)

      const timelineObj = {
        pidm: user.value.pidm,
        name: user.value.name,
        date: new Date(),
        text: ``,
        icon: 'fal fa-exchange',
        color: 'info',
        visibleToStudent: true
      }
      if (/[0-9a-f]{24}/.test(advisorChangeId.value)) {
        // This is a MongoDB Object ID, so it is a change of advisor in the changes array
        const index = changes.value.findIndex(({ _id }) => _id === advisorChangeId.value)
        let oldName = ''
        let oldPidm = ''
        if (index >= 0) {
          const rec = changes.value[index]
          if (rec.field === 'major') {
            oldPidm = rec.advisor
            oldName = await getAdvrName(rec.advisor)
            let text = rec.text.replace(oldName, newName)
            changes.value.splice(index, 1, { ...rec, advisor: pidm, text })
          } else if (rec.field === 'advisor') {
            oldPidm = rec.value
            oldName = await getAdvrName(rec.value)
            let text = rec.text.replace(oldName, newName)
            changes.value.splice(index, 1, { ...rec, value: pidm, text })
          }
          timelineObj.text = `Advisor changed from ${oldName} to ${newName}`
          timeline.value.splice(0, 0, timelineObj)
          const apprIndex = advisorApprovals.value.findIndex(({ pidm: p }) => p === oldPidm)
          advisorApprovals.value.splice(apprIndex, 1, { ...advisorApprovals.value[apprIndex], removed: true })
          advisorApprovals.value.splice(apprIndex, 0, { pidm, name: newName, approved: false })
          // We need to do this in two steps because MongoDB can't push and pull to the same array at the same time
          // So first, pull to remove the old advisor approval and make the change in the changes array
          await service.patch(id.value, {
            'changes.$[el].advisor': pidm,
            $pull: { advisorApproval: { pidm: oldPidm } }
          }, { query: { arrayFilters: [{ 'el._id': advisorChangeId.value }] } })
          // Then push the new advisor approval and timeline entry
          await service.patch(id.value, {
            $push: { timeline: timelineObj, advisorApproval: { pidm, name: newName } }
          })
        }
      } else {
        // This is a pidm, so must be an addition or change in advisor approval
        if (advisorChangeId.value === '') {
          advisorApprovals.value.push({ pidm, name: newName, approved: false })
          timelineObj.text = `Added Advisor Approval: ${newName}`
          await service.patch(id.value, { $push: { advisorApproval: { pidm, name: newName }, timeline: timelineObj } })
        } else {
          const index = advisorApprovals.value.findIndex(({ pidm: p }) => p === advisorChangeId.value)
          if (index >= 0) {
            const { pidm: oldPidm, name: oldName } = advisorApprovals.value[index]
            timelineObj.text = `Changed Advisor Approval from ${oldName} to ${newName}`
            await service.patch(id.value, { $push: { timeline: timelineObj, advisorApproval: { pidm, name: newName } }, $pull: { advisorApproval: { pidm: oldPidm } } })
            advisorApprovals.value.splice(index, 1, { pidm, name: newName, approved: false })
          }
        }
      }
      changeAdvisorDialog.value = false
    }

    function advisorApprovalChange (pidm) {
      advisorChangeId.value = pidm
      newAdvisor.value = ''
      changeAdvisorDialog.value = true
    }

    async function advisorApprovalRemove (pidm) {
      const index = advisorApprovals.value.findIndex(({ pidm: p }) => pidm === p)
      if (index >= 0) {
        advisorApprovals.value.splice(index, 1, { ...advisorApprovals.value[index], removed: true })
        const timelineObj = {
          pidm: user.value.pidm,
          name: user.value.name,
          date: new Date(),
          text: `Removed ${advisorApprovals.value[index].name} from required Advisor Approvals`,
          icon: 'fal fa-exchange',
          color: 'info',
          visibleToStudent: true
        }
        await service.patch(id.value, { $pull: { advisorApproval: { pidm } }, $push: { timeline: timelineObj } })
      }
    }

    async function advisorApprovalAdd (pidm) {
      const name = await getAdvrName(pidm)
      const timelineObj = {
        pidm: user.value.pidm,
        name: user.value.name,
        date: new Date(),
        text: `Added advisor ${name} back to required Advisor Approvals`,
        icon: 'fal fa-exchange',
        color: 'info',
        visibleToStudent: true
      }
      const temp = await service.patch(id.value, { $push: { advisorApproval: { pidm, name }, timeline: timelineObj } })
      const rec = temp.advisorApproval.find(({ pidm: p }) => p === pidm)
      const index = advisorApprovals.value.findIndex(({ pidm: p }) => pidm === p)
      if (index >= 0) {
        advisorApprovals.value.splice(index, 1, rec)
      } else {
        advisorApprovals.value.push(rec)
      }
    }

    const isSendingToAdvisors = ref(false)
    async function sendToAdvisors () {
      isSendingToAdvisors.value = true
      // Email the advisors with a link to this entry
      // This will just be one single email with all of the advisors grouped together
      // But first we need to get their email addresses from the directory
      const to = await getEmail(advisorApprovals.value.filter(({ removed }) => !removed).map(({ pidm }) => pidm))
      await root.$feathers.service('system/email').create({
        to,
        from: 'recordsoffice@covenant.edu',
        subject: `Program Change for ${name.value} Requires Your Approval`,
        html: `<p>A program change for ${name.value} has just been reviewed by the Records office and requires your approval.</p>${employeeLink.value}<br/>Please respond to this request at your earliest convenience.<br/>${signature.value}`
      })
      // Change the status and add to the timeline that this was passed on to the advisors for approval
      const timelineObj = {
        pidm: user.value.pidm,
        name: user.value.name,
        date: new Date(),
        text: `Sent to advisors for approval:<br/>${to.join('<br/>')}`,
        icon: 'fal fa-envelope',
        color: 'success',
        visibleToStudent: true
      }
      await service.patch(id.value, { status: 'Pending', $push: { timeline: timelineObj } })
      status.value = 'Pending'
      timeline.value.splice(0, 0, timelineObj)
      isSendingToAdvisors.value = false
    }

    async function grantAdvisorApproval (pidm, advName) {
      const timelineObj = {
        pidm: user.value.pidm,
        name: user.value.name,
        date: new Date(),
        text: 'Advisor approval/acknowledgement granted',
        icon: 'fal fa-check',
        color: 'success',
        visibleToStudent: true
      }
      const patch = { $push: { timeline: timelineObj }, 'advisorApproval.$[el].approved': true, 'advisorApproval.$[el].date': new Date() }
      const arrayFilters = [{ 'el.pidm': user.value.pidm }]
      if (pidm) {
        // This means the Records office clicked the button to approve it for the faculty member
        timelineObj.text += ` on behalf of ${advName}`
        arrayFilters.splice(0, 1, { 'el.pidm': pidm })
      }
      const rec = await service.patch(id.value, patch, { query: { arrayFilters } })
      timeline.value.splice(0, 0, timelineObj)
      advisorApprovals.value = rec.advisorApproval
      const remaining = rec.advisorApproval.filter(({ approved }) => !approved)
      if (remaining.length === 0) {
        await service.patch(id.value, { status: 'Approved' })
        if (!isRecords.value) {
          await root.$feathers.service('system/email').create({
            to: 'recordsoffice@covenant.edu',
            from: 'report-email@covenant.edu',
            subject: `Final Program/Advisor Change Approval Granted for ${name.value}`,
            html: `<p>The final approval for the program/advisor change for ${name} has been given, so it is now ready to update in Banner.</p> ${employeeLink.value}`
          })
        }
        status.value = 'Approved'
      }
    }

    const returnDialog = ref(false)
    const returnSendEmail = ref(true)
    const returnMessage = ref('')
    async function returnToStudent () {
      const timelineObj = {
        pidm: user.value.pidm,
        name: user.value.name,
        date: new Date(),
        text: `Returned to student`,
        icon: 'fal fa-step-backward',
        color: 'info',
        visibleToStudent: true
      }
      if (returnSendEmail.value && returnMessage.value.length > 0) {
        timelineObj.text += ': ' + returnMessage.value
      }
      await service.patch(id.value, { status: 'Returned', $push: { timeline: timelineObj } })
      status.value = 'Returned'
      timeline.value.splice(0, 0, timelineObj)
      if (returnSendEmail.value) {
        await root.$feathers.service('system/email').create({
          to: getEmail(pidm.value),
          from: 'recordsoffice@covenant.edu',
          subject: 'Program/Advisor Change Returned',
          html: `<p>Your Program/Advisor change request has been returned to you. Here's the message that was sent with this:</p>${commentBox(returnMessage.value)}<br/>${studentLink.value}<br/>${signature.value}`
        })
      }
    }

    const processDialog = ref(false)
    const processComment = ref('')
    async function markProcessed () {
      const timelineObj = {
        pidm: user.value.pidm,
        name: user.value.name,
        date: new Date(),
        text: `Marked Complete`,
        icon: 'fal fa-shield-check',
        color: 'success',
        visibleToStudent: true
      }
      // If a process comment ws entered, send an email to the student
      if (processComment.value) {
        timelineObj.text += ':<br/>' + processComment.value.replaceAll('\n', '<br/>')
        await root.$feathers.service('system/email').create({
          to: await getEmail(pidm.value),
          from: 'recordsofffice@covenant.edu',
          subject: 'Program/Advisor Change Completed',
          html: `<p>Your program/advisor change request has been updated in Banner. Please allow a few hours for it to also update in the Portal. Below is the final message from the Records office: </p>${commentBox(processComment.value)}<br/>${studentLink.value}<br/>${signature.value}`
        })
      }
      // Update the status to be processed
      await service.patch(id.value, { status: 'Processed', $push: { timeline: timelineObj } })
      timeline.value.splice(0, 0, timelineObj)
      status.value = 'Processed'
      processDialog.value = false
    }

    return {
      id,
      user,
      roles,
      isRecords,
      isPendingApprover,
      current,
      dataLoaded,
      pidm,
      bannerId,
      name,
      term,
      termLabel,
      status,
      changes,
      timeline,
      advisorApprovals,
      majorItems,
      minorItems,
      showAddMessage,
      comment,
      visibleToStudent,
      emailStudent,
      emailAdvisors,
      addComment,
      toDateString,
      advisorChangeId,
      changeAdvisorDialog,
      newAdvisor,
      openAdvisorChangeDialog,
      changeAdvisor,
      advisorApprovalChange,
      advisorApprovalRemove,
      advisorApprovalAdd,
      isSendingToAdvisors,
      sendToAdvisors,
      grantAdvisorApproval,
      returnDialog,
      returnSendEmail,
      returnMessage,
      returnToStudent,
      processDialog,
      processComment,
      markProcessed
    }
  }
}
</script>
